'use client';
import React, { useState, useRef, useCallback } from 'react';

interface ImageInfo {
  file: File;
  url: string;
  width: number;
  height: number;
  size: number;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export default function ImageResizeTool() {
  const [image, setImage] = useState<ImageInfo | null>(null);
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [keepAspect, setKeepAspect] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadImage = useCallback((file: File) => {
    if (!file.type.match(/^image\/(jpeg|png|webp)$/)) {
      alert('Please upload a JPG, PNG, or WebP image.');
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      setImage({ file, url, width: img.naturalWidth, height: img.naturalHeight, size: file.size });
      setWidth(String(img.naturalWidth));
      setHeight(String(img.naturalHeight));
    };
    img.src = url;
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) loadImage(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) loadImage(file);
  };

  const handleWidthChange = (val: string) => {
    setWidth(val);
    if (keepAspect && image && val) {
      const ratio = image.height / image.width;
      setHeight(String(Math.round(Number(val) * ratio)));
    }
  };

  const handleHeightChange = (val: string) => {
    setHeight(val);
    if (keepAspect && image && val) {
      const ratio = image.width / image.height;
      setWidth(String(Math.round(Number(val) * ratio)));
    }
  };

  const handleResize = async () => {
    if (!image || !width || !height) return;
    setProcessing(true);
    try {
      const img = new window.Image();
      img.src = image.url;
      await new Promise<void>((resolve) => { img.onload = () => resolve(); if (img.complete) resolve(); });
      const canvas = document.createElement('canvas');
      canvas.width = Number(width);
      canvas.height = Number(height);
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas not supported');
      ctx.drawImage(img, 0, 0, Number(width), Number(height));
      const outputType = image.file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => { if (b) resolve(b); else reject(new Error('Failed')); }, outputType, 0.92);
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = outputType === 'image/png' ? 'png' : 'jpg';
      a.download = `resized_${image.file.name.replace(/\.[^.]+$/, '')}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('Resize failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      {!image ? (
        <div
          className={`upload-zone rounded-2xl p-10 text-center cursor-pointer ${isDragging ? 'drag-over' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
          </div>
          <p className="font-semibold text-text-primary mb-1">Upload Image</p>
          <p className="text-text-muted text-sm mb-3">Drag & drop or click to browse</p>
          <p className="text-text-muted text-xs">Supports JPG, PNG, WebP</p>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFileChange} />
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <div className="w-full sm:w-40 h-32 rounded-xl overflow-hidden bg-gray-50 border border-gray-100 flex-shrink-0">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={image.url} alt="Preview" className="w-full h-full object-contain" />
            </div>
            <div className="flex-1 space-y-2">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-text-primary text-sm truncate max-w-[200px]">{image.file.name}</p>
                <button onClick={() => { setImage(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} className="text-text-muted hover:text-red-500 transition-colors text-xs">Remove</button>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-surface rounded-xl p-3">
                  <p className="text-text-muted text-xs mb-0.5">Original Size</p>
                  <p className="font-semibold text-text-primary">{formatBytes(image.size)}</p>
                </div>
                <div className="bg-surface rounded-xl p-3">
                  <p className="text-text-muted text-xs mb-0.5">Dimensions</p>
                  <p className="font-semibold text-text-primary">{image.width} × {image.height}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="font-semibold text-text-primary text-sm">New Dimensions</label>
              <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
                <input type="checkbox" checked={keepAspect} onChange={(e) => setKeepAspect(e.target.checked)} className="rounded" />
                Lock aspect ratio
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-text-muted mb-1 block">Width (px)</label>
                <input
                  type="number"
                  value={width}
                  onChange={(e) => handleWidthChange(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue"
                  min={1}
                />
              </div>
              <div>
                <label className="text-xs text-text-muted mb-1 block">Height (px)</label>
                <input
                  type="number"
                  value={height}
                  onChange={(e) => handleHeightChange(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue"
                  min={1}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {[{ label: '1920×1080', w: 1920, h: 1080 }, { label: '1280×720', w: 1280, h: 720 }, { label: '800×600', w: 800, h: 600 }, { label: '512×512', w: 512, h: 512 }].map((preset) => (
                <button key={preset.label} onClick={() => { setWidth(String(preset.w)); setHeight(String(preset.h)); setKeepAspect(false); }} className="text-xs px-3 py-1.5 rounded-lg bg-blue-50 text-brand-blue hover:bg-blue-100 transition-colors font-medium">
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={handleResize}
            disabled={processing || !width || !height}
            className="btn-primary w-full text-base py-3.5 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {processing ? (
              <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Resizing…</>
            ) : (
              <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" /></svg>Resize & Download</>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
